import { RadioGroup } from "@headlessui/react"
import React, { useContext, useEffect, useState } from "react"
import IPaymentMethods, { PaymentMethodType } from "../../../../interfaces/IPaymentMethods"
import IOrder from "../../../../interfaces/IOrder"
import { XMarkIcon } from "@heroicons/react/20/solid"
import { AdminContext } from "../../../../contexts/AdminContext"
import api from "../../../../api2"
import { useDebouncedCallback } from "use-debounce"
import * as Sentry from "@sentry/react"
import { CreditCardIcon, BuildingLibraryIcon } from "@heroicons/react/24/outline"

interface ComponentProps {
  paymentMethods: IPaymentMethods[]
  setPaymentMethods: React.Dispatch<React.SetStateAction<IPaymentMethods[]>>
  setSelectedPaymentMethodType: React.Dispatch<React.SetStateAction<PaymentMethodType>>
  order: IOrder
  camperId: string
  disabled?: boolean
  isStore?: boolean
}

const PaymentMethodsList = (props: ComponentProps) => {
  const { showConfirm, showNotification } = useContext(AdminContext)
  const [isLoading, setIsLoading] = useState(false)
  const camperId = props.camperId || props.order?.camper_id

  useEffect(() => {
    fetchPaymentMethods()
  }, [camperId])

  const fetchPaymentMethods = useDebouncedCallback(async () => {
    if (!camperId) {
      props.setPaymentMethods([])
      return
    }

    setIsLoading(true)

    try {
      const { payment_methods } = await api.Campers.fetchPaymentMethods(camperId)

      const filteredPaymentMethods = payment_methods.filter(
        (method) => !(props.isStore && method.type === "us_bank_account"),
      )

      props.setPaymentMethods(filteredPaymentMethods)
    } catch (err) {
      Sentry.captureException(err)
    }

    setIsLoading(false)
  }, 100)

  const handleCancelCreditCard = async (camperId: string, method: any) => {
    const isConfirmed = await showConfirm({
      title: "Are you sure you want to delete this credit card?",
      description: method.brand.toUpperCase() + " ending in " + method.last4,
      ctaLabel: "Remove",
      colorName: "red",
      ctaColorName: "red",
    })

    if (isConfirmed) {
      api.Campers.removeCreditCard(camperId, method.id)
        .then(() => {
          fetchPaymentMethods()
        })
        .catch((err) => {
          console.error(err)
          showNotification({
            type: "error",
            title: "Failed to remove credit card",
            description: err.message,
            dismissAfter: 5000,
          })
        })
    }
  }

  if (isLoading) {
    return <PaymentMethodPlaceholder />
  }

  const handlePaymentMethodChange = (type: PaymentMethodType) => {
    props.setSelectedPaymentMethodType(type)
  }

  return (
    <>
      {props.paymentMethods?.map((method, idx) => (
        <RadioGroup.Option key={`payment-method-${idx}`} value={method.id} onClick={() => handlePaymentMethodChange(method.type)} disabled={props.disabled}>
          {({ checked }) => (
            <div
              className={`py-2 flex justify-between rounded-lg ${props.disabled
                ? 'opacity-50 cursor-not-allowed'
                : 'cursor-pointer'
                } ${checked ? "border-2 border-green-600 font-semibold" : "border-gray-300 border"}`}
            >
              <div className="flex flex-row items-center">
                {method.type === 'us_bank_account' ? (
                  <BuildingLibraryIcon className="h-5 w-5 ml-3 mr-2" />
                ) : (
                  <CreditCardIcon className="h-5 w-5 ml-3 mr-2" />
                )}
                <div className="flex flex-col justify-center">
                  <div className="leading-4">
                    {method.brand.toUpperCase()} ending in {method.last4}
                  </div>
                  {method.type !== 'us_bank_account' && <div className="text-gray-400 text-xs font-normal">
                    Expires {method.exp_month}/{method.exp_year}
                  </div>}
                </div>
              </div>
              {camperId && (
                <XMarkIcon
                  className="h-8 w-8 text-gray-700 cursor-pointer mr-1 p-0.75 rounded-full hover:bg-gray-100"
                  onClick={() => handleCancelCreditCard(camperId, method)}
                />
              )}
            </div>
          )}
        </RadioGroup.Option>
      ))}
    </>
  )
}

const PaymentMethodPlaceholder = () => {
  return (
    <div className="py-2 flex justify-between rounded-lg border-gray-300 border">
      <div className="flex flex-row items-center">
        <CreditCardIcon className="h-5 w-5 ml-3 mr-2 text-gray-300" />
        <div className="flex flex-col justify-center">
          <div className="leading-4 mb-1">
            <div className="w-24 h-4 bg-gray-300 animate-pulse rounded-lg" />
          </div>
          <div className="text-gray-400 text-xs font-normal">
            <div className="w-16 h-2 bg-gray-300 animate-pulse rounded-lg" />
          </div>
        </div>
      </div>
      <XMarkIcon className="h-8 w-8 text-gray-700 cursor-pointer mr-1 p-0.75 rounded-full hover:bg-gray-100" />
    </div>
  )
}

export default PaymentMethodsList
